import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, BarChart3, Settings, QrCode, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  targetElement: string; // CSS selector for highlighting
  route?: string; // Optional route to navigate to
  position: 'top' | 'bottom' | 'left' | 'right';
}

const tourSteps: TourStep[] = [
  {
    id: 'welcome',
    title: '🎉 Welcome to NexQR!',
    description: 'Let\'s take a quick tour to help you get started. You can skip this anytime.',
    icon: <Sparkles className="h-6 w-6" />,
    targetElement: '',
    position: 'bottom',
  },
  {
    id: 'dashboard',
    title: '📊 Track Your Usage',
    description: 'Monitor your QR codes, scans, and monthly limits here. Stay on top of your usage!',
    icon: <BarChart3 className="h-6 w-6" />,
    targetElement: '[href="/dashboard"]',
    route: '/dashboard',
    position: 'bottom',
  },
  {
    id: 'usage-card',
    title: '📈 Usage Indicator',
    description: 'See your real-time usage: 5 static QR codes, 3 dynamic QR codes, and 50 scans per month on the free plan.',
    icon: <QrCode className="h-6 w-6" />,
    targetElement: '.usage-indicator-card',
    route: '/dashboard',
    position: 'left',
  },
  {
    id: 'settings',
    title: '⚙️ Upgrade Your Account',
    description: 'Need more? Upgrade to unlock unlimited QR codes, advanced analytics, and premium features.',
    icon: <Settings className="h-6 w-6" />,
    targetElement: '[href="/settings"]',
    route: '/settings',
    position: 'bottom',
  },
];

export function OnboardingTour() {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if tour should be shown
    const showTour = localStorage.getItem('nexqr_show_tour');
    const tourCompleted = localStorage.getItem('nexqr_tour_completed');
    
    if (showTour === 'true' && !tourCompleted) {
      // Small delay to ensure page is loaded
      setTimeout(() => {
        setIsActive(true);
        localStorage.removeItem('nexqr_show_tour');
      }, 1000);
    }
  }, []);

  useEffect(() => {
    if (!isActive) return;

    const step = tourSteps[currentStep];
    
    // Navigate to required route if specified
    if (step.route && window.location.pathname !== step.route) {
      navigate(step.route);
    }

    // Highlight target element
    if (step.targetElement) {
      setTimeout(() => {
        const element = document.querySelector(step.targetElement) as HTMLElement;
        if (element) {
          setHighlightedElement(element);
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    } else {
      setHighlightedElement(null);
    }
  }, [currentStep, isActive, navigate]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handleSkip = () => {
    completeTour();
  };

  const completeTour = () => {
    setIsActive(false);
    setHighlightedElement(null);
    localStorage.setItem('nexqr_tour_completed', 'true');
  };

  if (!isActive) return null;

  const step = tourSteps[currentStep];
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  return (
    <>
      {/* Overlay */}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
          onClick={handleSkip}
        />
      </AnimatePresence>

      {/* Highlight spotlight */}
      {highlightedElement && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed z-[9999] pointer-events-none"
          style={{
            top: highlightedElement.offsetTop - 8,
            left: highlightedElement.offsetLeft - 8,
            width: highlightedElement.offsetWidth + 16,
            height: highlightedElement.offsetHeight + 16,
            boxShadow: '0 0 0 4px rgba(139, 92, 246, 0.5), 0 0 0 9999px rgba(0, 0, 0, 0.6)',
            borderRadius: '12px',
          }}
        />
      )}

      {/* Tour Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[10000] w-full max-w-md mx-4"
        >
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl border-2 border-primary/20 overflow-hidden">
            {/* Progress Bar */}
            <div className="h-1 bg-zinc-200 dark:bg-zinc-800">
              <motion.div
                className="h-full gradient-hero"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Close Button */}
              <button
                onClick={handleSkip}
                className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Icon */}
              <div className="flex items-center justify-center w-16 h-16 rounded-full gradient-hero text-white mb-4">
                {step.icon}
              </div>

              {/* Title & Description */}
              <h3 className="text-2xl font-bold mb-2 text-zinc-900 dark:text-white">
                {step.title}
              </h3>
              <p className="text-zinc-600 dark:text-zinc-400 mb-6 leading-relaxed">
                {step.description}
              </p>

              {/* Step Counter */}
              <div className="flex items-center justify-between mb-6">
                <span className="text-sm text-zinc-500 dark:text-zinc-400">
                  Step {currentStep + 1} of {tourSteps.length}
                </span>
                <div className="flex gap-1.5">
                  {tourSteps.map((_, index) => (
                    <div
                      key={index}
                      className={`h-1.5 w-8 rounded-full transition-colors ${
                        index === currentStep
                          ? 'gradient-hero'
                          : index < currentStep
                          ? 'bg-primary/40'
                          : 'bg-zinc-200 dark:bg-zinc-700'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleSkip}
                  className="flex-1"
                >
                  Skip Tour
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1 gradient-hero"
                >
                  {currentStep < tourSteps.length - 1 ? (
                    <>
                      Next <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  ) : (
                    'Get Started'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
}
